export * from "./repos.js";
export * from "./admin.js";
import { z } from "zod";
import { EVENT_SEVERITIES, RUN_SOURCES, RUN_STATUSES, SPAN_STATUSES, } from "../constants/index.js";
export const runStatusSchema = z.enum(RUN_STATUSES);
export const runSourceSchema = z.enum(RUN_SOURCES);
export const spanStatusSchema = z.enum(SPAN_STATUSES);
export const eventSeveritySchema = z.enum(EVENT_SEVERITIES);
export const runsListQuerySchema = z.object({
    // One status or a comma list (`ok,error`), so a caller that wants "finished
    // runs" gets a full page of them from the server rather than filtering a page
    // that running rows have already used up.
    status: z
        .string()
        .transform((s) => s.split(","))
        .pipe(z.array(runStatusSchema).min(1))
        .optional(),
    limit: z.coerce.number().int().min(1).max(200).optional().default(50),
    offset: z.coerce.number().int().min(0).safe().optional().default(0),
});
export const runIdParamSchema = z.object({
    trace_id: z
        .string()
        .min(1)
        .regex(/^[0-9a-fA-F]+$/, "trace_id must be hex"),
});
// `.safe()` on `after_id` here and `offset` above, the two params with no upper
// bound: `.int()` alone admits `1e21` and `2**63`, which node-postgres sends as
// `"1e+21"` / `"9223372036854776000"` and Postgres rejects as a bigint — a 500
// for what is a malformed request. `events.id` is a `mode: "number"` bigserial,
// so no real id sits above MAX_SAFE_INTEGER anyway.
export const eventsAfterQuerySchema = z.object({
    after_id: z.coerce.number().int().nonnegative().safe().optional().default(0),
    limit: z.coerce.number().int().min(1).max(1000).optional().default(200),
});
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
    }),
});
// Inbound WebSocket frames a browser may send on /api/live. The server only
// acts on `ping` (replies `pong`); validating here keeps the last inbound
// boundary zod-checked per the repo convention. Matches the WsClientMessage type.
export const wsClientMessageSchema = z.object({
    type: z.enum(["ping", "pong"]),
});
// Outbound WebSocket frames the server pushes to a browser on /api/live. A
// `snapshot` frame is written straight into the query cache and rendered, so
// this is the one inbound boundary that reaches the SPA without passing through
// a typed fetch — validate it here per the repo convention, mirroring
// `wsClientMessageSchema`. `satisfies z.ZodType<…>` fails the build if either
// schema drifts from its hand-written type.
export const runSummarySchema = z.object({
    trace_id: z.string(),
    name: z.string(),
    status: runStatusSchema,
    source: runSourceSchema,
    service_name: z.string(),
    host: z.string().nullable(),
    repo: z.string().nullable(),
    started_at: z.string(),
    ended_at: z.string().nullable(),
    duration_ms: z.number().nullable(),
    exit_code: z.number().nullable(),
    summary: z.string().nullable(),
    summary_status: z.enum(["pending", "ok", "error"]).nullable(),
    summary_model: z.string().nullable(),
    summary_generated_at: z.string().nullable(),
    summary_error: z.string().nullable(),
});
export const wsServerMessageSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("snapshot"), runs: z.array(runSummarySchema) }),
    z.object({
        type: z.literal("run-change"),
        trace_id: z.string(),
        op: z.enum(["INSERT", "UPDATE", "DELETE"]),
    }),
    z.object({ type: z.literal("pong") }),
]);
export const STATS_DAYS = [7, 30, 90];
export const statsQuerySchema = z.object({
    days: z.coerce
        .number()
        .refine((v) => STATS_DAYS.includes(v), { message: "days must be 7, 30, or 90" })
        .optional()
        .default(30),
});
//# sourceMappingURL=index.js.map